import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Divider } from "@mui/material";
import { TablePage } from "@outpave/ds";
import type { PageHeaderCrumb } from "@outpave/ds";
import type { ColorScheme } from "context/ColorSchemeContext";
import { ThemeColors } from "utils/themeContextHelper";
import { Columns } from "components/layout/reusableLayouts";
import BrandColorsPanel, {
  BrandColorKey,
  BrandColorsTab,
  ChartChoice,
} from "./BrandColorsPanel";
import BrandingLivePreview from "./BrandingLivePreview";
import { useDraftTokens } from "./useDraftTokens";
import { useThemePreset } from "theme/themePresetStore";

/** The branding page for company and platform; each passes its own logos, tabs, actions and save. */

export interface BrandingExtraTab {
  id: string;
  label: string;
  content: React.ReactNode;
}

interface Props {
  breadcrumbs?: PageHeaderCrumb[];
  /** Omit on a surface that already sits under its own page header. */
  title?: string;
  subtitle?: string;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  /** Rendered above the colour rows on the Brand tab only. */
  logos?: React.ReactNode;
  colors: Partial<ThemeColors>;
  /** The layer beneath this surface: the platform's record, for a company. */
  inherited?: Partial<ThemeColors>;
  inheritedLabel?: string;
  onColorChange: (key: BrandColorKey, hex: string) => void;
  chart: ChartChoice;
  onChartChange: (next: ChartChoice) => void;
  /** Surface-specific tabs, after the colour tabs. */
  extraTabs?: BrandingExtraTab[];
  previewLogoUrl?: string;
  previewLightLogoUrl?: string;
  /** Rendered after the columns: drawers, dialogs, toasts. */
  children?: React.ReactNode;
}

const COLOR_TABS: { id: BrandColorsTab; label: string }[] = [
  { id: "brand", label: "Brand" },
  { id: "status", label: "Status colors" },
  { id: "chart", label: "Chart colors" },
];

const panelSx = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  padding: "var(--space-6)",
  gap: "var(--space-5)",
} as const;

const BrandingPage: React.FC<Props> = ({
  breadcrumbs,
  title,
  subtitle,
  primaryAction,
  secondaryAction,
  logos,
  colors,
  inherited,
  inheritedLabel,
  onColorChange,
  chart,
  onChartChange,
  extraTabs = [],
  previewLogoUrl,
  previewLightLogoUrl,
  children,
}) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string>(COLOR_TABS[0].id);
  const [previewScheme, setPreviewScheme] = useState<ColorScheme>("dark");
  // The viewer's own theme, since that is how this browser will show the result.
  const themePreset = useThemePreset();
  const tokens = useDraftTokens(colors, inherited, previewScheme, themePreset);

  const tabs = [...COLOR_TABS, ...extraTabs];
  const colorTab = COLOR_TABS.find((candidate) => candidate.id === tab);
  const extraTab = extraTabs.find((candidate) => candidate.id === tab);
  // The card tab draws the card itself, so only the colour tabs get the preview.
  const showsPreview = Boolean(colorTab);

  return (
    <TablePage
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={subtitle}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      tabs={{
        items: tabs.map(({ id, label }) => ({ id, label: t(label) })),
        activeId: tab,
        onChange: setTab,
      }}
      // The content is a two-column layout of its own cards, so the shell's
      // bordered surface would be a box around boxes.
      wrapped={false}
    >
      {/* The editor keeps its width on every tab. */}
      <Columns
        count={{ xs: 1, lg: "1.15fr 1fr" }}
        gap="var(--space-4)"
        sx={{ marginTop: "var(--space-7)", alignItems: "start" }}
      >
        <Card variant="outlined" sx={panelSx}>
          {colorTab ? (
            <>
              {colorTab.id === "brand" && logos && (
                <>
                  {logos}
                  <Divider />
                </>
              )}
              <BrandColorsPanel
                tab={colorTab.id}
                value={colors}
                inherited={inherited}
                inheritedLabel={inheritedLabel}
                onChange={onColorChange}
                chart={chart}
                onChartChange={onChartChange}
                scheme={previewScheme}
                tokens={tokens}
              />
            </>
          ) : (
            extraTab?.content
          )}
        </Card>

        {showsPreview && (
          <Card variant="outlined" sx={panelSx}>
            <BrandingLivePreview
              tokens={tokens}
              scheme={previewScheme}
              onSchemeChange={setPreviewScheme}
              logoUrl={previewLogoUrl}
              lightLogoUrl={previewLightLogoUrl}
            />
          </Card>
        )}
      </Columns>

      {children}
    </TablePage>
  );
};

export default BrandingPage;
